import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Building2, Check, Edit, Plus, Trash2, X } from 'lucide-react';
import apiService from '../../services/api';
import {
  JUSTICE_SPHERES,
  justiceSphereLabel,
  matchTribunalJusticeSphere,
} from '../../utils/justiceSphere';

/** Ordem fixa das colunas (valores mínimos e tribunais). */
const JUSTICE_COLUMN_ORDER = ['federal', 'trabalho', 'comum'];

const JUSTICE_META = {
  federal: {
    label: justiceSphereLabel('federal'),
    color: 'bg-purple-50 border-purple-200',
    header: 'bg-purple-100 text-purple-800',
    badge: 'bg-purple-100 text-purple-700',
  },
  trabalho: {
    label: justiceSphereLabel('trabalho'),
    color: 'bg-green-50 border-green-200',
    header: 'bg-green-100 text-green-800',
    badge: 'bg-green-100 text-green-700',
  },
  comum: {
    label: justiceSphereLabel('comum'),
    color: 'bg-blue-50 border-blue-200',
    header: 'bg-blue-100 text-blue-800',
    badge: 'bg-blue-100 text-blue-700',
  },
};

function inferJusticeType(sigla) {
  return matchTribunalJusticeSphere(sigla) ?? 'comum';
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

function parseMinAcceptedValue(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return null;
  const parsed = Number(text);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

export default function TribunaisTab({ readOnly = false }) {
  const [tribunals, setTribunals] = useState([]);
  const [spheres, setSpheres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    sigla: '',
    name: '',
    justiceType: 'comum',
  });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    sigla: '',
    name: '',
    justiceType: 'comum',
    active: true,
  });
  const [editingSphereId, setEditingSphereId] = useState(null);
  const [sphereValue, setSphereValue] = useState('');

  async function fetchData({ silent = false } = {}) {
    try {
      if (!silent) setLoading(true);
      const [tribunalsData, spheresData] = await Promise.all([
        apiService.get('/settings/tribunals'),
        apiService.get('/settings/justice-spheres'),
      ]);
      setTribunals(tribunalsData || []);
      setSpheres(spheresData || []);
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao carregar tribunais e esferas');
      if (!silent) {
        setTribunals([]);
        setSpheres([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const grouped = useMemo(
    () => {
      const bySigla = (a, b) =>
        a.sigla.localeCompare(b.sigla, 'pt-BR', { numeric: true });
      return {
        federal: tribunals.filter((item) => item.justiceType === 'federal').sort(bySigla),
        trabalho: tribunals.filter((item) => item.justiceType === 'trabalho').sort(bySigla),
        comum: tribunals.filter((item) => item.justiceType === 'comum').sort(bySigla),
      };
    },
    [tribunals],
  );

  const orderedSpheres = useMemo(() => {
    const byType = Object.fromEntries(
      spheres.map((sphere) => [sphere.justiceType, sphere]),
    );
    return JUSTICE_COLUMN_ORDER.map((justiceType) => byType[justiceType]).filter(
      Boolean,
    );
  }, [spheres]);

  async function handleCreate(event) {
    event.preventDefault();
    if (!form.sigla.trim()) {
      setSuccess(null);
      setError('Informe a sigla do tribunal');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiService.post('/settings/tribunals', {
        sigla: form.sigla.trim(),
        name: form.name.trim() || null,
        justiceType: form.justiceType,
        active: true,
      });
      setForm({ sigla: '', name: '', justiceType: 'comum' });
      setShowForm(false);
      setSuccess('Tribunal cadastrado');
      await fetchData({ silent: true });
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao cadastrar tribunal');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(id) {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiService.put(`/settings/tribunals/${id}`, {
        sigla: editForm.sigla.trim(),
        name: editForm.name.trim() || null,
        justiceType: editForm.justiceType,
        active: editForm.active,
      });
      setEditingId(null);
      setSuccess('Tribunal atualizado');
      await fetchData({ silent: true });
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao atualizar tribunal');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(item) {
    try {
      setError(null);
      setSuccess(null);
      await apiService.put(`/settings/tribunals/${item.id}`, {
        active: !item.active,
      });
      setSuccess(item.active ? `${item.sigla} pausado na busca` : `${item.sigla} reativado na busca`);
      await fetchData({ silent: true });
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao atualizar status do tribunal');
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Excluir o tribunal ${item.sigla}? Ele deixará de ser consultado no fluxo.`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await apiService.delete(`/settings/tribunals/${item.id}`);
      setSuccess('Tribunal excluído');
      await fetchData({ silent: true });
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao excluir tribunal');
    }
  }

  async function handleSaveSphere(sphere) {
    const parsed = parseMinAcceptedValue(sphereValue);
    if (parsed === null) {
      setSuccess(null);
      setError('Informe um valor mínimo válido');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiService.put(`/settings/justice-spheres/${sphere.id}`, {
        minAcceptedValue: parsed,
      });
      setEditingSphereId(null);
      setSuccess(`Valor mínimo da ${sphere.label} atualizado`);
      await fetchData({ silent: true });
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao atualizar valor mínimo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-gray-600 max-w-3xl">
          {orderedSpheres.length > 0
            ? `Os tribunais ativos definem onde o sistema busca publicações. O valor mínimo de cada esfera vale para todo o fluxo: ${orderedSpheres
                .map((sphere) => `${sphere.label} em ${formatCurrency(sphere.minAcceptedValue)}`)
                .join('; ')}.`
            : 'Os tribunais ativos definem onde o sistema busca publicações. O valor mínimo de cada esfera vale para todo o fluxo.'}
        </p>
        {!readOnly && (
          <button
            onClick={() => setShowForm((current) => !current)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
              showForm
                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? 'Fechar' : 'Novo Tribunal'}
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <Check className="w-4 h-4" />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {orderedSpheres.map((sphere) => {
          const meta = JUSTICE_META[sphere.justiceType] || JUSTICE_META.comum;
          const isEditing = editingSphereId === sphere.id;

          return (
            <div key={sphere.id} className={`${meta.color} border rounded-xl p-4`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Valor mínimo
                  </p>
                  <h3 className="text-sm font-semibold text-gray-900 mt-1">{sphere.label}</h3>
                </div>
                {!readOnly && !isEditing && (
                  <button
                    onClick={() => {
                      setEditingSphereId(sphere.id);
                      setSphereValue(String(Math.round(sphere.minAcceptedValue)));
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-white/70 rounded"
                    title="Editar valor mínimo"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="mt-3 space-y-2">
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    value={sphereValue}
                    onChange={(event) => setSphereValue(event.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSaveSphere(sphere)}
                      disabled={saving}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      Salvar
                    </button>
                    <button
                      onClick={() => setEditingSphereId(null)}
                      className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-700 rounded-lg"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-2xl font-bold text-gray-900">
                  {formatCurrency(sphere.minAcceptedValue)}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-600">
                Publicações abaixo deste valor nesta esfera não são aceitas.
              </p>
            </div>
          );
        })}
      </div>

      {showForm && !readOnly && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Novo tribunal</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sigla *
                </label>
                <input
                  type="text"
                  value={form.sigla}
                  onChange={(event) => {
                    const sigla = event.target.value.toUpperCase();
                    setForm((prev) => ({
                      ...prev,
                      sigla,
                      justiceType: inferJusticeType(sigla),
                    }));
                  }}
                  placeholder="Ex: TJSP, TRT3, TRF1"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                  placeholder="Opcional"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Esfera de justiça
                </label>
                <select
                  value={form.justiceType}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, justiceType: event.target.value }))
                  }
                  className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {JUSTICE_SPHERES.map((sphere) => (
                    <option key={sphere.id} value={sphere.id}>
                      {sphere.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              <Plus className="w-4 h-4" />
              {saving ? 'Cadastrando...' : 'Cadastrar Tribunal'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Tribunais monitorados</h2>
          <p className="text-sm text-gray-600">
            {tribunals.length} {tribunals.length === 1 ? 'tribunal' : 'tribunais'} ·{' '}
            {tribunals.filter((item) => item.active).length} ativos na busca
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              <p className="mt-2 text-gray-600">Carregando tribunais...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {JUSTICE_COLUMN_ORDER.map((justiceType) => {
                const items = grouped[justiceType] || [];
                const meta = JUSTICE_META[justiceType];
                return (
                  <div key={justiceType} className={`${meta.color} border rounded-lg overflow-hidden`}>
                    <div className={`${meta.header} px-3 py-2 flex items-center justify-between`}>
                      <span className="font-semibold text-xs">{meta.label}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-white/70 min-w-[18px] text-center font-medium">
                        {items.length}
                      </span>
                    </div>
                    <div className="p-2 space-y-1.5 min-h-[200px]">
                      {items.length === 0 ? (
                        <div className="text-center py-6 text-gray-400">
                          <Building2 className="w-6 h-6 mx-auto mb-1 opacity-30" />
                          <p className="text-xs">Nenhum tribunal nesta esfera</p>
                        </div>
                      ) : (
                        items.map((item) => (
                          <div
                            key={item.id}
                            className="bg-white p-2 rounded border border-gray-200 hover:shadow-sm transition-all group"
                          >
                            {editingId === item.id ? (
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  value={editForm.sigla}
                                  onChange={(event) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      sigla: event.target.value.toUpperCase(),
                                    }))
                                  }
                                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded bg-white text-gray-900"
                                />
                                <input
                                  type="text"
                                  value={editForm.name}
                                  onChange={(event) =>
                                    setEditForm((prev) => ({ ...prev, name: event.target.value }))
                                  }
                                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded bg-white text-gray-900"
                                  placeholder="Nome"
                                />
                                <select
                                  value={editForm.justiceType}
                                  onChange={(event) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      justiceType: event.target.value,
                                    }))
                                  }
                                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded bg-white text-gray-900"
                                >
                                  {JUSTICE_SPHERES.map((sphere) => (
                                    <option key={sphere.id} value={sphere.id}>
                                      {sphere.label}
                                    </option>
                                  ))}
                                </select>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleUpdate(item.id)}
                                    disabled={saving}
                                    className="p-0.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                                    title="Salvar"
                                  >
                                    <Check className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => setEditingId(null)}
                                    className="p-0.5 text-gray-400 hover:bg-gray-50 rounded"
                                    title="Cancelar"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h4 className="font-medium text-gray-900 text-xs leading-tight">
                                    {item.sigla}
                                  </h4>
                                  {item.name && (
                                    <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                                      {item.name}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-1 mt-1">
                                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.badge}`}>
                                      {meta.label}
                                    </span>
                                    <span
                                      className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        item.active
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-gray-100 text-gray-500'
                                      }`}
                                    >
                                      {item.active ? 'Ativo' : 'Inativo'}
                                    </span>
                                  </div>
                                </div>
                                {!readOnly && (
                                  <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-all">
                                    <button
                                      onClick={() => handleToggleActive(item)}
                                      className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded text-[10px] px-1"
                                      title={item.active ? 'Pausar na busca' : 'Reativar na busca'}
                                    >
                                      {item.active ? 'Pausar' : 'Ativar'}
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingId(item.id);
                                        setEditForm({
                                          sigla: item.sigla,
                                          name: item.name || '',
                                          justiceType: item.justiceType,
                                          active: item.active,
                                        });
                                      }}
                                      className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                      title="Editar"
                                    >
                                      <Edit className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(item)}
                                      className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                      title="Excluir"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
